import { useEffect, useState } from "react";

/**
 * Silent protection layer:
 * - No toasts, no warning overlays, no visible messages.
 * - Disables right-click, common copy/save/print shortcuts, drag, PrintScreen.
 * - Hides the page on print (anti-PDF), disables text selection.
 * - Blurs content when the window loses focus (anti background screenshot tools).
 *
 * IMPORTANT: this is client-side only. Real scraping protection is enforced
 * server-side in the edge functions (UA blocklist, rate-limiting, X-Robots-Tag).
 */
export const SecurityProtection = ({ disabled = false }: { disabled?: boolean } = {}) => {
  const [isBlurred, setIsBlurred] = useState(false);

  useEffect(() => {
    if (disabled) return;

    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault();
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;
      const mod = e.ctrlKey || (isMac && e.metaKey);
      const k = e.key.toLowerCase();

      // Ctrl/Cmd + P (print) / S (save) / U (view-source)
      if (mod && (k === "p" || k === "s" || k === "u")) {
        e.preventDefault();
        return;
      }

      // Copy a non-empty selection
      if (mod && k === "c") {
        const selection = window.getSelection()?.toString();
        if (selection) {
          e.preventDefault();
        }
        return;
      }

      // PrintScreen — overwrite clipboard silently
      if (e.key === "PrintScreen" || e.keyCode === 44) {
        e.preventDefault();
        navigator.clipboard?.writeText?.("").catch(() => {});
        return;
      }

      // DevTools shortcuts
      if (
        (e.ctrlKey && e.shiftKey && (k === "i" || k === "j" || k === "c")) ||
        (isMac && e.metaKey && e.altKey && (k === "i" || k === "j" || k === "c")) ||
        e.key === "F12"
      ) {
        e.preventDefault();
        return;
      }
    };

    const handleDragStart = (e: DragEvent) => {
      e.preventDefault();
    };

    const handleBlur = () => setIsBlurred(true);
    const handleFocus = () => setIsBlurred(false);

    document.addEventListener("contextmenu", handleContextMenu);
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("dragstart", handleDragStart);
    window.addEventListener("blur", handleBlur);
    window.addEventListener("focus", handleFocus);

    return () => {
      document.removeEventListener("contextmenu", handleContextMenu);
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("dragstart", handleDragStart);
      window.removeEventListener("blur", handleBlur);
      window.removeEventListener("focus", handleFocus);
    };
  }, [disabled]);

  if (disabled) return null;



  return (
    <>
      <style>{`
        @media print {
          body { display: none !important; }
        }
        body {
          -webkit-touch-callout: none;
          -webkit-user-select: none;
          -khtml-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
          user-select: none;
        }
      `}</style>

      {isBlurred && (
        <div
          aria-hidden="true"
          className="fixed inset-0 z-[99999] backdrop-blur-xl bg-background/80 pointer-events-none transition-all duration-200"
        />
      )}
    </>
  );
};

export default SecurityProtection;
